import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, type CartTotalsDto, type MenuProduct } from "../api";

/**
 * Корзина гостя, собранная из нескольких заведений парка (п. 7, 9 ТЗ).
 *
 * Состав хранится в браузере и переживает случайное закрытие вкладки —
 * гость собирает заказ, отвлекается на разговор, возвращается. А вот суммы
 * всегда считает сервер: цены, стоп-лист и режим работы живут в базе и
 * меняются партнёром, повторять их правила в браузере — врать о цене.
 *
 * Позиция помнит, из какого она заведения: без этого корзину нельзя разложить
 * по частям заказа, а разложить её надо ещё до оплаты — гость должен понимать,
 * что еду понесут три разных человека из трёх разных кухонь.
 */

export interface ChosenModifier {
  groupId: string;
  optionId: string;
  title: string;
  price: number;
  qty: number;
}

export interface CartItem {
  pointId: string;
  pointName: string;
  productId: string;
  title: string;
  imageUrl: string | null;
  unitPrice: number;
  qty: number;
  modifiers: ChosenModifier[];
}

const STORAGE_KEY = "pf.cart.v1";
const RECALC_DELAY_MS = 250;

const EMPTY: CartTotalsDto = {
  points: [],
  baseSubtotal: 0,
  partnerDiscount: 0,
  platformDiscount: 0,
  total: 0,
  promo: null
};

/** Две позиции одинаковы, если совпали заведение, товар и весь набор добавок. */
function sameItem(left: CartItem, right: CartItem): boolean {
  if (left.pointId !== right.pointId || left.productId !== right.productId) return false;
  if (left.modifiers.length !== right.modifiers.length) return false;

  const key = (item: CartItem) =>
    item.modifiers
      .map(modifier => `${modifier.groupId}:${modifier.optionId}:${modifier.qty}`)
      .sort()
      .join("|");

  return key(left) === key(right);
}

function readStored(parkId: string): CartItem[] {
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY}.${parkId}`);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed as CartItem[]) : [];
  } catch {
    // Испорченное хранилище — не повод не пустить гостя в меню.
    return [];
  }
}

export function useCart(parkId: string) {
  /*
   * Корзина хранится отдельно для каждого парка.
   *
   * Гость мог быть вчера в другом парке, и товары оттуда здесь не продаются
   * вовсе: сервер отверг бы всю корзину целиком, а гость увидел бы ошибку
   * вместо меню и не понял бы, при чём тут он.
   */
  const [items, setItems] = useState<CartItem[]>(() => readStored(parkId));
  const [totals, setTotals] = useState<CartTotalsDto>(EMPTY);
  const [pricing, setPricing] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  /*
   * Промокод уходит на сервер вместе с составом.
   *
   * Считать по нему скидку в браузере нельзя: сроки, лимиты и область
   * действия лежат в базе и меняются владельцем акции. Повторять эти правила
   * здесь значит однажды показать гостю цену, которой нет.
   */
  const [promoCode, setPromoCode] = useState("");
  const [promoError, setPromoError] = useState<string | null>(null);

  const timer = useRef<number | undefined>(undefined);

  useEffect(() => {
    localStorage.setItem(`${STORAGE_KEY}.${parkId}`, JSON.stringify(items));
  }, [items, parkId]);

  const payload = useMemo(
    () =>
      items.map(item => ({
        pointId: item.pointId,
        productId: item.productId,
        qty: item.qty,
        modifiers: item.modifiers.map(modifier => ({
          groupId: modifier.groupId,
          optionId: modifier.optionId,
          qty: modifier.qty
        }))
      })),
    [items]
  );

  useEffect(() => {
    if (payload.length === 0) {
      setTotals(EMPTY);
      setProblem(null);
      return;
    }

    // Пересчёт не дёргается на каждое нажатие: гость крутит количество
    // быстрее, чем отвечает мобильная сеть.
    window.clearTimeout(timer.current);
    setPricing(true);

    timer.current = window.setTimeout(() => {
      api
        .calculate(parkId, payload, promoCode.trim() || undefined)
        .then(next => {
          setTotals(next);
          setProblem(null);
          setPromoError(null);
        })
        .catch((error: Error) => {
          /*
           * Негодный промокод не должен ломать корзину.
           *
           * Гость ввёл «ЛЕТО» наугад — состав от этого не испортился, и
           * прятать за ошибкой кода весь заказ значит заставить его собирать
           * корзину заново.
           */
          if (promoCode.trim()) {
            setPromoError(error.message);
            api
              .calculate(parkId, payload)
              .then(next => {
                setTotals(next);
                setProblem(null);
              })
              .catch(() => setProblem(error.message));
            return;
          }

          setProblem(error.message);
        })
        .finally(() => setPricing(false));
    }, RECALC_DELAY_MS);

    return () => window.clearTimeout(timer.current);
  }, [payload, parkId, promoCode]);

  const add = useCallback(
    (
      product: MenuProduct,
      point: { id: string; name: string },
      modifiers: ChosenModifier[],
      qty = 1
    ) => {
      setItems(current => {
        const incoming: CartItem = {
          pointId: point.id,
          pointName: point.name,
          productId: product.id,
          title: product.title,
          imageUrl: product.imageUrl,
          unitPrice: product.price + modifiers.reduce((sum, m) => sum + m.price * m.qty, 0),
          qty,
          modifiers
        };

        const index = current.findIndex(item => sameItem(item, incoming));
        if (index === -1) return [...current, incoming];

        return current.map((item, position) =>
          position === index ? { ...item, qty: item.qty + qty } : item
        );
      });
    },
    []
  );

  const setQty = useCallback((index: number, qty: number) => {
    setItems(current =>
      qty <= 0
        ? current.filter((_, position) => position !== index)
        : current.map((item, position) => (position === index ? { ...item, qty } : item))
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const count = items.reduce((sum, item) => sum + item.qty, 0);

  /** Сколько позиций этого товара уже в корзине — для отметки в меню. */
  const countOf = useCallback(
    (productId: string) =>
      items.reduce((sum, item) => (item.productId === productId ? sum + item.qty : sum), 0),
    [items]
  );

  return {
    items,
    totals,
    pricing,
    problem,
    count,
    countOf,
    add,
    setQty,
    clear,
    promoCode,
    setPromoCode,
    promoError
  };
}
